import { DomainTypeEnum } from '@pypestream/api-services';
import {
  BlockList,
  Button,
  Icon,
  Spacer,
  Stack,
  TextOverline,
} from '@pypestream/design-system';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { OutputProjectFormType } from '../..';
import { ProjectEnvironmentField } from '../create-project-flow';
import { ProjectFormButtons } from './project-form-buttons';
import { ProjectTitles } from './project-titles';

const defaultDomain = { type: DomainTypeEnum.Domain, url: '' };

export type ProjectEnvironmentsPropsType = {
  nextStep: (formData?: Partial<OutputProjectFormType>) => void;
  prevStep: () => void;
};

export const ProjectEnvironments = ({
  nextStep,
  prevStep,
}: ProjectEnvironmentsPropsType) => {
  const [t] = useTranslation();
  const form = useFormContext();

  const { control, handleSubmit } = form;

  const productionField = useFieldArray({
    control,
    name: 'projectEnvironmentConfigs.0.domains',
    rules: {
      minLength: 1,
    },
  });

  const stageField = useFieldArray({
    control,
    name: 'projectEnvironmentConfigs.1.domains',
  });

  const developmentField = useFieldArray({
    control,
    name: 'projectEnvironmentConfigs.2.domains',
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ProjectTitles
        step="project-environments"
        title={
          t('manager/projects:createProject.step2.title', {
            defaultValue: 'Environments URLs',
          }) || ''
        }
        subTitle={
          t('manager/projects:createProject.subTitle', {
            defaultValue:
              'Projects organize work around an online property like a website. Everything in the project must be tied to a domain and, optionally, its subdomains. You can make changes in project settings.',
          }) || ''
        }
      />
      <Spacer size="xlarge" />
      <TextOverline style={{ textTransform: 'uppercase' }}>
        <TranslationComponent i18nKey="manager/projects:createProject.step2.field1">
          Production
        </TranslationComponent>
      </TextOverline>
      <BlockList gutter="small">
        {productionField.fields.map(({ id }, index) => {
          const isTheFirstEl = index === 0;

          return (
            <Stack
              key={id}
              gutter="2xsmall"
              alignItems="center"
              justifyContent="space-between"
              nowrap
            >
              <ProjectEnvironmentField
                name="projectEnvironmentConfigs.0.domains"
                index={index}
                remove={productionField.remove}
                isForbiddenForRemove={!index}
                form={form}
                required={isTheFirstEl}
                placeholder={
                  isTheFirstEl ? 'https://www.pypestream.com/product/' : ''
                }
              />
            </Stack>
          );
        })}
      </BlockList>
      <Spacer size="small" />
      <Button
        variant="ghost"
        size="large"
        onClick={() => productionField.append(defaultDomain)}
      >
        <TranslationComponent i18nKey="manager/projects:createProject.step2.field1Button">
          Add Production URL
        </TranslationComponent>
        <Icon slot="prefix" name="add" />
      </Button>
      <Spacer size="xlarge" />
      <TextOverline style={{ textTransform: 'uppercase' }}>
        <TranslationComponent i18nKey="manager/projects:createProject.step2.field2">
          Staging
        </TranslationComponent>
      </TextOverline>
      <BlockList gutter="small">
        {stageField.fields.map(({ id }, index) => (
          <Stack
            key={id}
            gutter="2xsmall"
            alignItems="center"
            justifyContent="space-between"
            nowrap
          >
            <ProjectEnvironmentField
              name="projectEnvironmentConfigs.1.domains"
              index={index}
              remove={stageField.remove}
              form={form}
            />
          </Stack>
        ))}
      </BlockList>
      <Spacer size="small" />
      <Button
        variant="ghost"
        size="large"
        onClick={() => stageField.append(defaultDomain)}
      >
        <TranslationComponent i18nKey="manager/projects:createProject.step2.field2Button">
          Add Staging URL
        </TranslationComponent>
        <Icon slot="prefix" name="add" />
      </Button>
      <Spacer size="xlarge" />
      <TextOverline style={{ textTransform: 'uppercase' }}>
        <TranslationComponent i18nKey="manager/projects:createProject.step2.field3">
          Development
        </TranslationComponent>
      </TextOverline>
      <BlockList gutter="small">
        {developmentField.fields.map(({ id }, index) => (
          <Stack
            key={id}
            gutter="2xsmall"
            alignItems="center"
            justifyContent="space-between"
            nowrap
          >
            <ProjectEnvironmentField
              name="projectEnvironmentConfigs.2.domains"
              index={index}
              remove={developmentField.remove}
              form={form}
            />
          </Stack>
        ))}
      </BlockList>
      <Spacer size="small" />
      <Button
        variant="ghost"
        size="large"
        onClick={() => developmentField.append(defaultDomain)}
      >
        <TranslationComponent i18nKey="manager/projects:createProject.step2.field3Button">
          Add Development URL
        </TranslationComponent>
        <Icon slot="prefix" name="add" />
      </Button>
      <Spacer size="xlarge" />
      <ProjectFormButtons
        step="project-environments"
        nextStep={handleSubmit(nextStep)}
        prevStep={prevStep}
      />
    </form>
  );
};
